import { belgeZorunluHatasi, yuvarla3, type BelgeKutugu, type KutukBelgeGirdisi } from "@oasis/domain";
import { cn } from "@/lib/cn";

/**
 * D-045 … D-049: Registers ekranlarının ortak saf yardımcıları (Kutukler.tsx, kutuk-stok.tsx, kutuk-belgeleri.tsx).
 * JSX yok; sınıf adları statik sayfalardan (kutukler--pasif, --sarf-belge, --stok-paket) birebir.
 */

/** Satır sonu yuvası: çöp kutusu ya da "used in …"; bütün kütük tablolarında aynı genişlik, Edit ve Deactivate her satırda hizalı. */
export const YUVA = "inline-flex w-28 justify-end whitespace-normal text-right leading-tight";

/** Belge çipi (tabloda, PDF bağlantısı). */
export const CIP_SINIFI = "rounded bg-info-bg px-1.5 py-0.5 text-xs text-info";

/** Eksik belge rozeti: "no PO" gri, öteki rozetler ("no certificate", "no qualification document", …) uyarı tonunda. */
export const rozetSinifi = (rozet: string) =>
  rozet === "no PO" ? "rounded bg-surface-2 px-1.5 py-0.5 text-xs text-ink-3" : "rounded bg-warn-bg px-1.5 py-0.5 text-xs text-warn";

/** Pasif kaydın ve biten paketin gri rozeti (inline-block: iki satıra kırılınca zemini bölünmesin). */
export const GRI_ROZET = "inline-block rounded bg-surface-2 px-1.5 py-0.5 text-xs text-ink-3";

/** "Show inactive (n)" / "Show finished (n)": basılıyken sekmeler ve Komuta filtreleri gibi lacivert; sıfırda devre dışı görünür, gizlenmez. */
export const gosterDugmesiSinifi = (basili: boolean) =>
  cn(
    "inline-flex h-kontrol-kucuk items-center gap-1.5 rounded-md border px-2.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-45",
    basili ? "border-navy bg-navy text-white dark:text-[#0a141c]" : "border-line bg-surface text-ink hover:border-line-strong",
  );

/** Kaydın tarihini en yeni belgeden alan form alanı (D-049): kaynakçıda End of validity, alette Calibration end. */
export const TARIH_ALANI: Partial<Record<BelgeKutugu, string>> = { kaynakci: "gecerlilikSonu", alet: "kalibrasyonSonu" };

/** Miktar gösterimi: 3 ondalık (60.266 + 60 = 120.266); boşsa "—". */
export const miktarMetni = (n: number | null | undefined) => (n === null || n === undefined ? "—" : String(yuvarla3(n)));

/** İşaretli fark: "+60", "−4" (eksi işareti U+2212), "0". */
export function farkMetni(n: number) {
  const d = yuvarla3(n);
  return d > 0 ? `+${d}` : d < 0 ? `−${Math.abs(d)}` : "0";
}

/** Sayı girişi: boş → null; virgül ondalık kabul edilir; okunamayan değer NaN (sunucu hata metnini verir). */
export function sayiOku(s: string): number | null {
  const t = s.trim().replace(",", ".");
  return t ? Number(t) : null;
}

/** Tarih girişinin değeri (YYYY-MM-DD): ISO metin ya da gün; boşsa "". */
export const gunGirdisi = (v: string | null | undefined) => (v ? v.slice(0, 10) : "");

/** Bugün, yerel gün olarak YYYY-MM-DD ("Add package" penceresinde Arrival date varsayılanı). */
export function bugun(simdi = new Date()) {
  return `${simdi.getFullYear()}-${String(simdi.getMonth() + 1).padStart(2, "0")}-${String(simdi.getDate()).padStart(2, "0")}`;
}

/**
 * Henüz kaydedilmemiş belge satırı (D-048 "Documents"): PDF yüklendi ya da "Use existing PDF" ile seçilecek; alanlar metin olarak tutulur.
 * Yeni kayıtta kaydın Save'iyle, kayıtlı kayıtta satırın "Add" düğmesiyle ya da pencerenin Save'iyle eklenir.
 */
export interface BelgeTaslagi {
  kimlik: number;
  tur: string;
  dokumanId: string;
  /** PDF adı (yüklenen) */
  ad: string;
  toplam: number;
  /** "Use existing PDF" ile açıldı: PDF satırda listeden seçilir */
  mevcuttan: boolean;
  sayfaBas: string;
  sayfaSon: string;
  belgeNo: string;
  duzenleyen: string;
  tarih: string;
  gecerlilikSonu: string;
  standart: string;
  /** D-064: NDT sertifikasının yöntemi (MT | PT | UT | VT | RT); seçilmemişse ve öteki kütüklerde "" */
  yontem: string;
  /** D-064: NDT sertifikasının seviyesi ("1" | "2" | "3"); seçilmemişse ve öteki kütüklerde "" */
  seviye: string;
  poNo: string;
  not: string;
}

let sayac = 0;

/** Yeni taslak: yüklenen PDF'le (pdf) ya da boş PDF seçimiyle ("Use existing PDF"). */
export function yeniTaslak(tur: string, pdf: { id: string; ad: string; toplam: number } | null): BelgeTaslagi {
  sayac += 1;
  return {
    kimlik: sayac, tur, dokumanId: pdf?.id ?? "", ad: pdf?.ad ?? "", toplam: pdf?.toplam ?? 0, mevcuttan: !pdf,
    sayfaBas: "", sayfaSon: "", belgeNo: "", duzenleyen: "", tarih: "", gecerlilikSonu: "", standart: "", yontem: "", seviye: "", poNo: "", not: "",
  };
}

/** PDF'i seçilmiş taslak: kaydın Save'i yalnız bunları ekler ("Use existing PDF" ile açılıp PDF seçilmemiş satır yok sayılır). */
export const taslakTamam = (t: BelgeTaslagi) => Boolean(t.tur && t.dokumanId);

/**
 * D-064: taslağın eksik zorunlu seçimi: NDT sertifikasında "select the method" ya da "select the level"; eksik yoksa ve öteki kütüklerde
 * null. Kural alan paketinde tek fonksiyondur (belgeZorunluHatasi; sunucu aynısını okur). Yalnız satırın "Add" düğmesini pasif eder;
 * `taslakTamam`a katılmaz: o süzgeçtir, genişlerse seçimi eksik sertifika Save'de sessizce atlanırdı (sunucu "Documents: select the method" der).
 */
export const taslakEksigi = (kutuk: BelgeKutugu, t: BelgeTaslagi) =>
  belgeZorunluHatasi(kutuk, { tur: t.tur, yontem: t.yontem || null, seviye: t.seviye || null });

const metin = (s: string) => s.trim() || null;

/** Taslağı ekleme gövdesine çevirir (boş metin → null, sayfalar sayı). Alan süzgeci ve denetim sunucuda (belgeAlanlari, zod). */
export function taslakGirdisi(t: BelgeTaslagi): KutukBelgeGirdisi {
  return {
    tur: t.tur,
    dokumanId: t.dokumanId,
    sayfaBas: sayiOku(t.sayfaBas),
    sayfaSon: sayiOku(t.sayfaSon),
    belgeNo: metin(t.belgeNo),
    duzenleyen: metin(t.duzenleyen),
    tarih: metin(t.tarih),
    gecerlilikSonu: metin(t.gecerlilikSonu),
    standart: metin(t.standart),
    yontem: metin(t.yontem),
    seviye: metin(t.seviye),
    poNo: metin(t.poNo),
    not: metin(t.not),
  };
}
